import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Mail,
  HardDrive,
  MessageSquare,
  Send,
  Video,
  FileText,
  Calendar,
  Phone,
  Hash,
  FileSignature,
  Activity,
  Filter,
  RefreshCw,
  Loader2,
  Clock,
  User,
  ArrowLeft,
  ExternalLink,
  ChevronDown,
  Inbox,
  FolderOpen,
  Bell,
  CheckCircle,
  AlertCircle,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;

// Activity type configurations
const ACTIVITY_TYPES = {
  email_received: {
    icon: Mail,
    color: 'text-red-500',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    label: 'Email Received',
    source: 'Gmail'
  },
  email_sent: {
    icon: Send,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    label: 'Email Sent',
    source: 'Gmail'
  },
  file_uploaded: {
    icon: HardDrive,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    label: 'File Uploaded',
    source: 'Google Drive'
  },
  file_shared: {
    icon: FolderOpen,
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    label: 'File Shared',
    source: 'Google Drive'
  },
  video_uploaded: {
    icon: Video,
    color: 'text-purple-500',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    label: 'Video Uploaded',
    source: 'Drive/Assessment'
  },
  slack_message: {
    icon: Hash,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    label: 'Slack Message',
    source: 'Slack'
  },
  slack_mention: {
    icon: Bell,
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    label: 'Mentioned in Slack',
    source: 'Slack'
  },
  discord_message: {
    icon: MessageSquare,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    label: 'Discord Message',
    source: 'Discord'
  },
  telegram_message: {
    icon: Send,
    color: 'text-sky-500',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-200',
    label: 'Telegram Message',
    source: 'Telegram'
  },
  calendar_event: {
    icon: Calendar,
    color: 'text-green-500',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    label: 'Calendar Event',
    source: 'Google Calendar'
  },
  interview_scheduled: {
    icon: Calendar,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    label: 'Interview Scheduled',
    source: 'Calendar'
  },
  call_made: {
    icon: Phone,
    color: 'text-teal-500',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    label: 'Call Made',
    source: 'Phone'
  },
  document_signed: {
    icon: FileSignature,
    color: 'text-orange-500',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    label: 'Document Signed',
    source: 'RabbitSign'
  },
  document_sent: {
    icon: FileText,
    color: 'text-pink-500',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-200',
    label: 'Document Sent',
    source: 'RabbitSign'
  },
  assessment_completed: {
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    label: 'Assessment Completed',
    source: 'Assessments'
  },
  status_changed: {
    icon: Activity,
    color: 'text-violet-500',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    label: 'Status Changed',
    source: 'CRM'
  },
  note_added: {
    icon: FileText,
    color: 'text-slate-500',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
    label: 'Note Added',
    source: 'CRM'
  }
};

const CandidateActivityFeed = ({ candidate, onBack }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (candidate) {
      fetchActivities();
    }
  }, [candidate]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch from universal activity endpoint
      const response = await axios.get(
        `${API}/api/candidates/${candidate.id}/activities`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setActivities(response.data || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
      // If endpoint doesn't exist yet, show empty state
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      
      // Trigger sync for all connected integrations
      await axios.post(
        `${API}/api/candidates/${candidate.id}/sync-activities`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Syncing activities from all integrations...');
      
      // Refetch after a short delay
      setTimeout(() => {
        fetchActivities();
        setRefreshing(false);
      }, 2000);
    } catch (error) {
      toast.error('Failed to sync activities');
      setRefreshing(false);
    }
  };

  const getFilteredActivities = () => {
    if (filter === 'all') return activities;
    
    const filterMap = {
      'emails': ['email_received', 'email_sent'],
      'files': ['file_uploaded', 'file_shared', 'video_uploaded'],
      'messages': ['slack_message', 'slack_mention', 'discord_message', 'telegram_message'],
      'calendar': ['calendar_event', 'interview_scheduled'],
      'documents': ['document_signed', 'document_sent'],
      'crm': ['status_changed', 'note_added', 'assessment_completed', 'call_made']
    };
    
    const allowedTypes = filterMap[filter] || [];
    return activities.filter(a => allowedTypes.includes(a.type));
  };

  const filteredActivities = getFilteredActivities();

  const getActivityConfig = (type) => {
    return ACTIVITY_TYPES[type] || {
      icon: Activity,
      color: 'text-gray-500',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      label: type,
      source: 'Unknown'
    };
  };

  const formatActivityTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffHours = (now - date) / (1000 * 60 * 60);
      
      if (diffHours < 24) {
        return formatDistanceToNow(date, { addSuffix: true });
      } else if (diffHours < 48) {
        return 'Yesterday at ' + format(date, 'h:mm a');
      } else {
        return format(date, 'MMM d, yyyy h:mm a');
      }
    } catch {
      return timestamp;
    }
  };

  const groupActivitiesByDate = (activities) => {
    const groups = {};
    
    activities.forEach(activity => {
      const date = new Date(activity.timestamp);
      const dateKey = format(date, 'yyyy-MM-dd');
      const dateLabel = format(date, 'EEEE, MMMM d, yyyy');
      
      if (!groups[dateKey]) {
        groups[dateKey] = {
          label: dateLabel,
          activities: []
        };
      }
      groups[dateKey].activities.push(activity);
    });
    
    // Sort by date descending
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, value]) => ({ date: key, ...value }));
  };

  const groupedActivities = groupActivitiesByDate(filteredActivities);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="candidate-activity-feed">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack} className="p-2">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Activity className="h-5 w-5 text-violet-500" />
              Activity Feed
            </h2>
            <p className="text-sm text-slate-500">
              All interactions with <span className="font-medium text-slate-700">{candidate.candidate_name}</span>
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Filter Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Filter className="h-4 w-4" />
                {filter === 'all' ? 'All Activities' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setFilter('all')}>
                <Activity className="h-4 w-4 mr-2" /> All Activities
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setFilter('emails')}>
                <Mail className="h-4 w-4 mr-2 text-red-500" /> Emails
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter('files')}>
                <HardDrive className="h-4 w-4 mr-2 text-yellow-500" /> Files & Videos
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter('messages')}>
                <MessageSquare className="h-4 w-4 mr-2 text-purple-500" /> Messages
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter('calendar')}>
                <Calendar className="h-4 w-4 mr-2 text-green-500" /> Calendar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter('documents')}>
                <FileSignature className="h-4 w-4 mr-2 text-orange-500" /> Documents
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter('crm')}>
                <User className="h-4 w-4 mr-2 text-violet-500" /> CRM Actions
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Refresh Button */}
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Sync
          </Button>
        </div>
      </div>

      {/* Candidate Info Card */}
      <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-100">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-violet-500 flex items-center justify-center text-white font-bold text-lg">
                {candidate.candidate_name?.charAt(0)?.toUpperCase() || 'C'}
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">{candidate.candidate_name}</h3>
                <div className="flex items-center gap-3 text-sm text-slate-500">
                  {candidate.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="h-3 w-3" /> {candidate.email}
                    </span>
                  )}
                  {candidate.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {candidate.phone}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right">
              <Badge variant="outline" className="bg-white">
                {candidate.company_name || 'No Company'}
              </Badge>
              <p className="text-xs text-slate-400 mt-1">
                {activities.length} activities tracked
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {[
          { key: 'emails', icon: Mail, color: 'text-red-500', bg: 'bg-red-50', count: activities.filter(a => a.type?.includes('email')).length },
          { key: 'files', icon: HardDrive, color: 'text-yellow-500', bg: 'bg-yellow-50', count: activities.filter(a => a.type?.includes('file') || a.type?.includes('video')).length },
          { key: 'messages', icon: MessageSquare, color: 'text-purple-500', bg: 'bg-purple-50', count: activities.filter(a => a.type?.includes('message') || a.type?.includes('slack') || a.type?.includes('discord')).length },
          { key: 'calendar', icon: Calendar, color: 'text-green-500', bg: 'bg-green-50', count: activities.filter(a => a.type?.includes('calendar') || a.type?.includes('interview')).length },
          { key: 'documents', icon: FileSignature, color: 'text-orange-500', bg: 'bg-orange-50', count: activities.filter(a => a.type?.includes('document') || a.type?.includes('signed')).length },
          { key: 'calls', icon: Phone, color: 'text-teal-500', bg: 'bg-teal-50', count: activities.filter(a => a.type?.includes('call')).length }
        ].map(stat => (
          <Card 
            key={stat.key} 
            className={`cursor-pointer hover:shadow-md transition-all ${filter === stat.key ? 'ring-2 ring-violet-400' : ''}`}
            onClick={() => setFilter(filter === stat.key ? 'all' : stat.key)}
          >
            <CardContent className="p-3 flex items-center gap-2">
              <div className={`p-2 rounded-lg ${stat.bg}`}>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </div>
              <div>
                <p className="text-lg font-bold text-slate-800">{stat.count}</p>
                <p className="text-xs text-slate-500 capitalize">{stat.key}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Activity Timeline */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-400" />
              Activity Timeline
            </span>
            <Badge variant="outline">{filteredActivities.length} items</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredActivities.length === 0 ? (
            <div className="text-center py-12">
              <Inbox className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No activities found</p>
              <p className="text-sm text-slate-400 mt-1">
                {filter !== 'all' 
                  ? 'Try selecting a different filter or sync from integrations'
                  : 'Activities will appear here when you interact with this candidate'}
              </p>
              <Button 
                variant="outline" 
                onClick={handleRefresh}
                className="mt-4 gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Sync from Integrations
              </Button>
            </div>
          ) : (
            <ScrollArea className="h-[500px] pr-4">
              <div className="space-y-6">
                {groupedActivities.map((group) => (
                  <div key={group.date}>
                    {/* Date Header */}
                    <div className="sticky top-0 bg-white z-10 py-2">
                      <div className="flex items-center gap-2">
                        <div className="h-px flex-1 bg-slate-200" />
                        <span className="text-xs font-medium text-slate-400 px-2">{group.label}</span>
                        <div className="h-px flex-1 bg-slate-200" />
                      </div>
                    </div>
                    
                    {/* Activities for this date */}
                    <div className="space-y-3 mt-3">
                      {group.activities.map((activity, idx) => {
                        const config = getActivityConfig(activity.type);
                        const Icon = config.icon;
                        
                        return (
                          <div
                            key={activity.id || idx}
                            className={`flex gap-3 p-3 rounded-xl border ${config.borderColor} ${config.bgColor} hover:shadow-sm transition-all`}
                          >
                            {/* Icon */}
                            <div className={`p-2 rounded-lg bg-white shadow-sm`}>
                              <Icon className={`h-5 w-5 ${config.color}`} />
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <p className="font-medium text-slate-800 text-sm">
                                    {activity.title || config.label}
                                  </p>
                                  {activity.description && (
                                    <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">
                                      {activity.description}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-2 mt-1">
                                    <Badge variant="outline" className="text-xs bg-white">
                                      {config.source}
                                    </Badge>
                                    <span className="text-xs text-slate-400">
                                      {formatActivityTime(activity.timestamp)}
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Action button */}
                                {activity.url && (
                                  <a 
                                    href={activity.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="p-1.5 hover:bg-white rounded-lg transition-colors"
                                  >
                                    <ExternalLink className="h-4 w-4 text-slate-400 hover:text-violet-500" />
                                  </a>
                                )}
                              </div>
                              
                              {/* Metadata */}
                              {activity.metadata && (
                                <div className="mt-2 p-2 bg-white rounded-lg text-xs text-slate-500">
                                  {activity.metadata.from && (
                                    <p>From: {activity.metadata.from}</p>
                                  )}
                                  {activity.metadata.to && (
                                    <p>To: {activity.metadata.to}</p>
                                  )}
                                  {activity.metadata.subject && (
                                    <p>Subject: {activity.metadata.subject}</p>
                                  )}
                                  {activity.metadata.file_name && (
                                    <p>File: {activity.metadata.file_name}</p>
                                  )}
                                  {activity.metadata.channel && (
                                    <p>Channel: #{activity.metadata.channel}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CandidateActivityFeed;
